import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, tokenStore } from "../../lib/api";
import { fmtBRL, fmtNumber, fmtDateTime, fmtDateShort } from "../../lib/format";
import { useFilters, periodToDates } from "../../lib/filters";
import type {
  CalculationMemoryDto,
  CalculationMemoryGroupDetailsDto,
  AuditStatus,
} from "@bi/types";

/**
 * Cards cujo valor é a soma de um INTERVALO — só neles o período do filtro global
 * se aplica. Os demais (posição em aberto, vencido, saldo, projeção 30d, janelas
 * fixas de 30 dias) têm janela definida pela própria regra do card.
 */
const PERIOD_AWARE_CARDS = new Set([
  "recebido-no-periodo",
  "pago-no-periodo",
  "faturamento-cte",
  "cte-emitidos",
  "cartas-frete",
]);

// ── helpers ──────────────────────────────────────────────────────────────────

function statusBar(status: AuditStatus, variancePct: number | null, lastAuditAt: string | null) {
  const last = lastAuditAt ? fmtDateTime(lastAuditAt) : null;

  if (status === "error") {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-100 text-gray-500 text-sm">
        <span className="w-2 h-2 rounded-full bg-gray-400 flex-shrink-0" />
        <span>
          Validação cruzada indisponível
          {last ? ` — última validação bem-sucedida em ${last}` : " — nenhuma validação registrada"}
        </span>
      </div>
    );
  }

  const pct = variancePct !== null ? Math.abs(variancePct).toFixed(2) : "—";

  if (status === "ok") {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-50 text-green-700 text-sm">
        <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
        <span>Em conformidade — variação de {pct}%</span>
        {last && <span className="ml-auto text-xs text-green-600 opacity-70">Auditado em {last}</span>}
      </div>
    );
  }

  if (status === "warning") {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 text-amber-700 text-sm">
        <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
        <span>Atenção — variação de {pct}%</span>
        {last && <span className="ml-auto text-xs text-amber-600 opacity-70">Auditado em {last}</span>}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 text-red-700 text-sm">
      <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
      <span>Crítico — variação de {pct}%</span>
      {last && <span className="ml-auto text-xs text-red-600 opacity-70">Auditado em {last}</span>}
    </div>
  );
}

function fmtSyncAt(iso: string | null) {
  if (!iso) return "—";
  return fmtDateTime(iso);
}

// Parse UTC date-only string as DD/MM (avoids timezone shift from new Date("YYYY-MM-DD"))
function fmtYMD(iso: string | null): string {
  if (!iso) return "—";
  const p = iso.split("T")[0]!.split("-");
  return `${p[2]}/${p[1]}`;
}

// Same but subtracts 1 day (for exclusive end date)
function fmtYMDEndExclusive(iso: string | null): string {
  if (!iso) return "—";
  const p = iso.split("T")[0]!.split("-");
  const dt = new Date(Date.UTC(Number(p[0]), Number(p[1]) - 1, Number(p[2]) - 1));
  const d = String(dt.getUTCDate()).padStart(2, "0");
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  return `${d}/${m}`;
}

function fmtValue(v: number, unit: "BRL" | "number" | undefined): string {
  if (unit === "number") return fmtNumber(v);
  return fmtBRL(v);
}

// ── componente principal ──────────────────────────────────────────────────────

export function CalculationMemoryPage() {
  const { cardKey = "" } = useParams<{ cardKey: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { filters } = useFilters();

  // Período vindo da tela de origem (?periodFrom&periodTo, ISO): é a janela INICIAL,
  // e cobre o drill por dia/faixa (ex.: uma faixa de vencimento), em que a janela não
  // é a do filtro global e precisa ser respeitada ao abrir.
  const [searchParams] = useSearchParams();
  const urlFrom = searchParams.get("periodFrom");
  const urlTo = searchParams.get("periodTo");

  // …mas o usuário pode trocar o período DENTRO do detalhamento (a barra de filtros
  // fica visível aqui, no AppShell). Antes a URL congelava a janela na navegação e a
  // troca não surtia efeito nenhum. Agora, assim que o filtro global muda em relação
  // ao que veio na URL, ele passa a mandar.
  const globalPeriod = periodToDates(filters.period, filters.customFrom, filters.customTo);
  const urlPeriodIsCurrent =
    !urlFrom || !urlTo || (urlFrom === globalPeriod.from && urlTo === globalPeriod.to);
  const [pinnedUrlPeriod, setPinnedUrlPeriod] = useState(Boolean(urlFrom && urlTo));
  useEffect(() => {
    // O filtro global mudou depois de abrir → solta a janela da URL, para sempre.
    if (!urlPeriodIsCurrent) setPinnedUrlPeriod(false);
  }, [urlPeriodIsCurrent]);

  const effectiveFrom = pinnedUrlPeriod && urlFrom ? urlFrom : globalPeriod.from;
  const effectiveTo = pinnedUrlPeriod && urlTo ? urlTo : globalPeriod.to;

  // Cards de janela FIXA (a vencer 30d / vencidos 30d / posição em aberto) não têm
  // período aplicável: mandar um sobrescreveria a regra do card. O backend só usa a
  // janela em cards `date_range`, e é ele quem informa se aplicou (`appliedFilters`).
  const periodApplies = Boolean(PERIOD_AWARE_CARDS.has(cardKey) || (urlFrom && urlTo));
  const periodQS = periodApplies
    ? `&periodFrom=${encodeURIComponent(effectiveFrom)}&periodTo=${encodeURIComponent(effectiveTo)}`
    : "";

  const [page, setPage] = useState(0);
  // ?group= permite chegar com um grupo já expandido (drill vindo de ranking)
  const [expandedGroup, setExpandedGroup] = useState<string | null>(searchParams.get("group"));
  const [detailPage, setDetailPage] = useState<Record<string, number>>({});
  const [csvLoading, setCsvLoading] = useState(false);

  // Trocar o período muda o conjunto de linhas: voltar para a primeira página e zerar
  // a paginação dos detalhes evita ficar numa página que já não existe ou mostrar um
  // subtotal do período anterior.
  useEffect(() => {
    setPage(0);
    setDetailPage({});
  }, [effectiveFrom, effectiveTo]);

  async function handleExportCsv() {
    setCsvLoading(true);
    try {
      const token = tokenStore.get();
      const res = await fetch(
        `/api/calculation-memory/${encodeURIComponent(cardKey)}/export/csv${periodQS ? `?${periodQS.slice(1)}` : ""}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `memoria-calculo-${cardKey}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erro ao exportar CSV:", err);
    } finally {
      setCsvLoading(false);
    }
  }
  const LIMIT = 50;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["calculation-memory", cardKey, page, effectiveFrom, effectiveTo],
    queryFn: () =>
      api.get<CalculationMemoryDto>(
        `/calculation-memory/${encodeURIComponent(cardKey)}?limit=${LIMIT}&offset=${page * LIMIT}&sortBy=value_desc${periodQS}`,
      ),
    staleTime: 60_000,
  });

  const reauditMutation = useMutation({
    mutationFn: () =>
      api.post<{ results: unknown[] }>(`/calculation-memory/${encodeURIComponent(cardKey)}/reaudit`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calculation-memory", cardKey] });
    },
  });

  const expandedDetailOffset = (groupKey: string) => (detailPage[groupKey] ?? 0) * LIMIT;

  const { data: detailData } = useQuery({
    queryKey: ["calculation-memory-detail", cardKey, expandedGroup, detailPage[expandedGroup ?? ""] ?? 0, effectiveFrom, effectiveTo],
    queryFn: () =>
      api.get<CalculationMemoryGroupDetailsDto>(
        `/calculation-memory/${encodeURIComponent(cardKey)}/group/${encodeURIComponent(expandedGroup!)}/details?limit=${LIMIT}&offset=${expandedDetailOffset(expandedGroup!)}${periodQS}`,
      ),
    enabled: expandedGroup !== null,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-32 bg-gray-100 rounded-xl" />
        <div className="h-24 bg-gray-100 rounded-xl" />
        <div className="h-64 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="card p-8 text-center">
        <p className="text-gray-500">Não foi possível carregar a memória de cálculo.</p>
        <button className="btn-secondary mt-4" onClick={() => navigate(-1)}>Voltar</button>
      </div>
    );
  }

  const { definition, value, crossValidation, breakdown } = data;
  const totalPages = Math.ceil(breakdown.pagination.totalGroups / LIMIT);

  return (
    <div className="space-y-6 max-w-5xl mx-auto">

      {/* ── Cabeçalho ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="flex-shrink-0 p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
            aria-label="Voltar"
          >
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
            </svg>
          </button>
          <nav className="text-sm text-gray-400 truncate">
            <span>Financeiro</span>
            <span className="mx-1">›</span>
            <span>Memória de Cálculo</span>
            <span className="mx-1">›</span>
            <span className="font-medium text-gray-700">{definition.displayName}</span>
          </nav>
        </div>
        <button
          className="btn-secondary flex items-center gap-1.5 flex-shrink-0"
          onClick={() => reauditMutation.mutate()}
          disabled={reauditMutation.isPending}
        >
          <svg className={`w-4 h-4 ${reauditMutation.isPending ? "animate-spin" : ""}`} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 01-9.201 2.466l-.312-.311h2.433a.75.75 0 000-1.5H3.989a.75.75 0 00-.75.75v4.242a.75.75 0 001.5 0v-2.43l.31.31a7 7 0 0011.712-3.138.75.75 0 00-1.449-.389zm1.23-3.723a.75.75 0 00.219-.53V2.929a.75.75 0 00-1.5 0V5.36l-.31-.31A7 7 0 003.239 8.188a.75.75 0 101.448.389A5.5 5.5 0 0113.89 6.11l.311.31h-2.432a.75.75 0 100 1.5h4.243a.75.75 0 00.53-.219z" clipRule="evenodd" />
          </svg>
          {reauditMutation.isPending ? "Auditando…" : "Reauditar"}
        </button>
      </div>

      {periodApplies && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-800">
          Recorte aplicado: <strong>{fmtYMD(effectiveFrom)} a {fmtYMDEndExclusive(effectiveTo)}</strong>. Os valores e a
          lista abaixo são só deste período — não do card inteiro.
          {pinnedUrlPeriod
            ? " Recorte veio da tela de origem; trocar o período no filtro acima passa a valer."
            : " Trocar o período no filtro acima atualiza esta tela."}
        </div>
      )}

      {/* ── Definição ──────────────────────────────────────────────────── */}
      <div className="card p-6 space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">O que é este número</p>
          <p className="text-sm text-gray-700 leading-relaxed">{definition.businessDefinition}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Como é calculado</p>
          <code className="block text-xs bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-800 font-mono leading-relaxed">
            {definition.formulaHuman}
          </code>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Filtros aplicados</p>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
              <span className="font-medium">Empresa:</span> {definition.appliedFilters.company}
            </span>
            {definition.filterType === "status" && (
              <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                <span className="font-medium">Status:</span> ABERTA (1)
              </span>
            )}
            {definition.filterType === "date_range" && (
              <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                <span className="font-medium">Período:</span>{" "}
                {fmtYMD(definition.appliedFilters.periodFrom)}{" "}
                a{" "}
                {fmtYMDEndExclusive(definition.appliedFilters.periodTo)}
              </span>
            )}
            {definition.filterType === "overdue" && (
              <>
                <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2.5 py-1 rounded-full">
                  <span className="font-medium">Status:</span> ABERTA (1)
                </span>
                <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2.5 py-1 rounded-full">
                  <span className="font-medium">Vencimento:</span> anterior a hoje
                </span>
              </>
            )}
            {definition.filterType === "balance" && (
              <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                <span className="font-medium">Contas:</span> ativas
              </span>
            )}
            {definition.filterType === "projection" && (
              <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                <span className="font-medium">Horizonte:</span>{" "}
                {fmtYMD(definition.appliedFilters.periodFrom)}{" "}
                a{" "}
                {fmtYMD(definition.appliedFilters.periodTo)}
              </span>
            )}
            {definition.filterType === "due_window" && (
              <>
                <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                  <span className="font-medium">Status:</span> ABERTA (1)
                </span>
                <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                  <span className="font-medium">Vencimento:</span>{" "}
                  {fmtYMD(definition.appliedFilters.periodFrom)} a {fmtYMDEndExclusive(definition.appliedFilters.periodTo)}
                </span>
              </>
            )}
            {definition.filterType === "overdue_window" && (
              <>
                <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2.5 py-1 rounded-full">
                  <span className="font-medium">Status:</span> ABERTA (1)
                </span>
                <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2.5 py-1 rounded-full">
                  <span className="font-medium">Vencimento:</span>{" "}
                  {fmtYMD(definition.appliedFilters.periodFrom)} a {fmtYMDEndExclusive(definition.appliedFilters.periodTo)} (vencidos)
                </span>
              </>
            )}
            <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">
              <span className="font-medium">Referência:</span> {fmtSyncAt(definition.appliedFilters.asOf)}
            </span>
          </div>
        </div>

        {/* Contrato de auditabilidade: campo de data, natureza, exclusões, limitações */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Campo de data</p>
            <p className="text-sm text-gray-700">{definition.dateFieldLabel}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Natureza</p>
            <p className="text-sm text-gray-700">
              {{ realized: "Realizado", forecast: "Previsto (contratual)", overdue: "Vencido",
                 due: "A vencer", open: "Em aberto (posição atual)", estimated: "Estimado" }[definition.calculationType] ?? definition.calculationType}
            </p>
          </div>
          {definition.excludedStatus.length > 0 && (
            <div className="sm:col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Fica de fora deste número</p>
              {definition.excludedStatus.map((e, i) => (
                <p key={i} className="text-xs text-gray-500">• {e}</p>
              ))}
            </div>
          )}
          {definition.limitations.length > 0 && (
            <div className="sm:col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 mb-1">Limitações</p>
              {definition.limitations.map((l, i) => (
                <p key={i} className="text-xs text-amber-700">⚠ {l}</p>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Valor + validação cruzada ───────────────────────────────────── */}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          {/* BI */}
          <div className="card p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Valor no BI</p>
            <p className="text-3xl font-bold text-gray-900">{fmtValue(value.total, definition.displayUnit)}</p>
            <p className="text-xs text-gray-400 mt-1">
              {value.recordCount.toLocaleString("pt-BR")}{" "}
              {definition.filterType === "date_range" ? "movimentos"
                : definition.filterType === "balance" ? "contas"
                : definition.filterType === "projection" ? "registros"
                : "títulos"}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Sync: {fmtSyncAt(value.lastSyncAt)}
              {value.source === "sinersoft" && " · total lido do ERP ao vivo"}
            </p>
            {value.detailGapPct > 0.005 && (
              <p className="text-xs text-amber-700 mt-2">
                {value.analyticRecordCount === 0 ? (
                  <>
                    ⚠️ O armazém não tem nenhum movimento neste período — a composição abaixo fica vazia.
                    O total é lido do ERP ao vivo e está correto; só o detalhamento depende da sincronização.
                  </>
                ) : (
                  <>
                    ⚠️ Os títulos listados abaixo somam {fmtValue(value.analyticTotal, definition.displayUnit)}
                    {" "}({value.analyticRecordCount.toLocaleString("pt-BR")}) — o snapshot ainda não reconciliou
                    com o ERP ({Math.round(value.detailGapPct * 1000) / 10}% de diferença). O total acima é o canônico.
                  </>
                )}
              </p>
            )}
          </div>

          {/* Sinersoft */}
          <div className="card p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Valor no Sinersoft</p>
            {crossValidation.sourceValue !== null ? (
              <>
                <p className="text-3xl font-bold text-gray-900">{fmtValue(crossValidation.sourceValue, definition.displayUnit)}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {crossValidation.sourceRecordCount?.toLocaleString("pt-BR") ?? "—"} registros
                </p>
              </>
            ) : (
              <p className="text-2xl font-bold text-gray-300">—</p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              Auditado: {fmtSyncAt(crossValidation.lastAuditAt)}
            </p>
          </div>
        </div>

        {/* Barra de status */}
        {statusBar(
          crossValidation.status as AuditStatus,
          crossValidation.variancePercentage,
          crossValidation.lastAuditAt,
        )}

        {crossValidation.variancePercentage !== null && crossValidation.status !== "error" && (
          <p className="text-xs text-gray-400 text-right">
            Tolerâncias do piloto: warning ≤ {crossValidation.warningTolerance}% · crítico &gt; {crossValidation.criticalTolerance}%
          </p>
        )}
      </div>

      {/* ── Composição por cliente ──────────────────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-surface-border flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Composição por{" "}
              {breakdown.groupBy === "supplier" ? "fornecedor"
                : breakdown.groupBy === "bank_account" ? "conta bancária"
                : breakdown.groupBy === "period" ? "período"
                : "cliente"}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {breakdown.pagination.totalGroups.toLocaleString("pt-BR")}{" "}
              {breakdown.groupBy === "supplier" ? "fornecedores"
                : breakdown.groupBy === "bank_account" ? "contas"
                : breakdown.groupBy === "period" ? "períodos"
                : "clientes"}{" "}
              ·{" "}
              {value.recordCount.toLocaleString("pt-BR")}{" "}
              {definition.filterType === "date_range" ? "movimentos"
                : definition.filterType === "balance" ? "contas"
                : definition.filterType === "projection" ? "registros"
                : "títulos"}{" "}
              · {fmtValue(value.total, definition.displayUnit)} total
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExportCsv}
              disabled={csvLoading}
              className="btn-secondary text-xs py-1.5"
            >
              {csvLoading ? "Exportando…" : "Exportar CSV"}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-border bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {breakdown.groupBy === "supplier" ? "Fornecedor"
                    : breakdown.groupBy === "bank_account" ? "Conta Bancária"
                    : breakdown.groupBy === "period" ? "Período"
                    : "Cliente"}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {breakdown.groupBy === "bank_account" ? "—" : "CNPJ/CPF"}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {definition.filterType === "date_range" ? "Valor Recebido/Pago"
                    : definition.filterType === "balance" ? "Saldo"
                    : definition.filterType === "projection" ? "Gap (Rec − Pag)"
                    : "Valor em Aberto"}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {definition.filterType === "date_range" ? "Movimentos"
                    : definition.filterType === "balance" ? "—"
                    : definition.filterType === "projection" ? "Registros"
                    : "Títulos"}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">% do Total</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {breakdown.rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center">
                    {value.analyticRecordCount === 0 && value.total !== 0 ? (
                      // Total veio do ERP ao vivo, composição vem do armazém: quando o
                      // armazém ainda não cobre o período, a tabela ficava simplesmente
                      // vazia, sem dizer por quê.
                      <>
                        <p className="text-sm text-gray-700 font-medium">
                          A composição deste período ainda não foi sincronizada.
                        </p>
                        <p className="text-xs text-gray-500 mt-1.5 max-w-lg mx-auto">
                          O total acima ({fmtValue(value.total, definition.displayUnit)}) é lido do ERP ao vivo,
                          mas o detalhamento vem do armazém, que não tem nenhum movimento neste período.
                          Última sincronização: <strong>{fmtSyncAt(value.lastSyncAt)}</strong>.
                        </p>
                        <p className="text-xs text-gray-500 mt-1.5">
                          Rode uma sincronização para ver os documentos que compõem o valor.
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-gray-400">
                        Nenhum movimento neste período.
                      </p>
                    )}
                  </td>
                </tr>
              )}
              {breakdown.rows.map((row) => {
                const isExpanded = expandedGroup === row.groupKey;
                return (
                  <React.Fragment key={row.groupKey}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setExpandedGroup(isExpanded ? null : row.groupKey);
                        if (!isExpanded) setDetailPage((p) => ({ ...p, [row.groupKey]: 0 }));
                      }}
                    >
                      <td className="px-4 py-3 font-medium text-gray-800 max-w-xs truncate">
                        {row.groupLabel}
                      </td>
                      <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                        {row.document ? fmtDocument(row.document) : "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">
                        {fmtValue(row.total, definition.displayUnit)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {row.recordCount.toLocaleString("pt-BR")}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {row.percentageOfTotal.toFixed(2)}%
                      </td>
                      <td className="px-4 py-3 text-center text-gray-400">
                        <svg
                          className={`w-4 h-4 mx-auto transition-transform ${isExpanded ? "rotate-90" : ""}`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                        </svg>
                      </td>
                    </tr>

                    {/* Subrow: duplicatas individuais */}
                    {isExpanded && (
                      <tr key={`${row.groupKey}-detail`}>
                        <td colSpan={6} className="bg-gray-50 px-6 py-3">
                          <DetailTable
                            data={detailData}
                            groupKey={row.groupKey}
                            detailPage={detailPage}
                            setDetailPage={setDetailPage}
                            limit={LIMIT}
                            displayUnit={definition.displayUnit}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="px-6 py-3 border-t border-surface-border flex items-center justify-between text-sm text-gray-500">
            <span>
              Página {page + 1} de {totalPages} ·{" "}
              {breakdown.pagination.totalGroups.toLocaleString("pt-BR")} grupos
            </span>
            <div className="flex gap-2">
              <button
                className="btn-secondary py-1 px-3 text-xs"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                Anterior
              </button>
              <button
                className="btn-secondary py-1 px-3 text-xs"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                Próximo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── sub-componente: tabela de duplicatas ──────────────────────────────────────

function DetailTable({
  data,
  groupKey,
  detailPage,
  setDetailPage,
  limit,
  displayUnit,
}: {
  data: CalculationMemoryGroupDetailsDto | undefined;
  groupKey: string;
  detailPage: Record<string, number>;
  setDetailPage: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  limit: number;
  displayUnit?: "BRL" | "number";
}) {
  if (!data) {
    return <p className="text-xs text-gray-400 py-2">Carregando registros…</p>;
  }

  const totalPages = Math.ceil(data.total / limit);
  const curPage = detailPage[groupKey] ?? 0;
  const isMovement = data.detailType === "movement";
  const isBalance = data.detailType === "balance";
  const isProjection = data.detailType === "projection";
  // Drill de grupo econômico: cada nota traz a filial (nome + CNPJ). Mostra a coluna
  // só quando há filial, para dar pra "ver as notas agrupadas por CNPJ/filial".
  const hasFilial = !isMovement && !isBalance && !isProjection && data.rows.some((r) => r.counterpartyName);

  return (
    <div className="space-y-2">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200 text-gray-400">
            <th className="py-1.5 text-left font-medium">
              {isBalance ? "Conta" : isProjection ? "Título" : "Chave"}
            </th>
            {isMovement ? (
              <>
                <th className="py-1.5 text-left font-medium">Data</th>
                <th className="py-1.5 text-left font-medium">Forma</th>
                <th className="py-1.5 text-right font-medium">Valor</th>
              </>
            ) : isBalance ? (
              <>
                <th className="py-1.5 text-left font-medium">Ag / Conta</th>
                <th className="py-1.5 text-right font-medium">Saldo</th>
              </>
            ) : isProjection ? (
              <>
                <th className="py-1.5 text-left font-medium">Tipo</th>
                <th className="py-1.5 text-left font-medium">Contraparte</th>
                <th className="py-1.5 text-left font-medium">Vencimento</th>
                <th className="py-1.5 text-right font-medium">Valor (± no gap)</th>
              </>
            ) : (
              <>
                {hasFilial && <th className="py-1.5 text-left font-medium">Filial</th>}
                <th className="py-1.5 text-left font-medium">Emissão</th>
                <th className="py-1.5 text-left font-medium">Vencimento</th>
                <th className="py-1.5 text-right font-medium">Valor Original</th>
                <th className="py-1.5 text-right font-medium">Liquidado</th>
                <th className="py-1.5 text-right font-medium">Saldo</th>
                <th className="py-1.5 text-right font-medium">Dias Atraso</th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.rows.map((r) => (
            <tr key={r.externalKey} className="text-gray-700">
              <td className="py-1.5 font-mono text-gray-400 max-w-[180px] truncate">
                {isBalance ? r.externalKey.substring(0, 8) + "…" : r.externalKey}
              </td>
              {isMovement ? (
                <>
                  <td className="py-1.5">{fmtDateShort(r.dueDate)}</td>
                  <td className="py-1.5 text-gray-500">{r.statusName}</td>
                  <td className="py-1.5 text-right font-medium">{fmtValue(r.openAmount, displayUnit)}</td>
                </>
              ) : isBalance ? (
                <>
                  <td className="py-1.5 text-gray-500">{r.statusName}</td>
                  <td className={`py-1.5 text-right font-medium ${r.openAmount < 0 ? "text-red-600" : "text-gray-900"}`}>
                    {fmtBRL(r.openAmount)}
                  </td>
                </>
              ) : isProjection ? (
                <>
                  <td className={`py-1.5 ${r.openAmount >= 0 ? "text-green-700" : "text-amber-700"}`}>
                    {r.statusName}
                  </td>
                  <td className="py-1.5 text-gray-600 max-w-[160px] truncate">{r.counterpartyName ?? "—"}</td>
                  <td className="py-1.5">{fmtDateShort(r.dueDate)}</td>
                  <td className={`py-1.5 text-right font-medium ${r.openAmount >= 0 ? "text-green-700" : "text-red-600"}`}>
                    {fmtBRL(r.openAmount)}
                  </td>
                </>
              ) : (
                <>
                  {hasFilial && (
                    <td className="py-1.5 text-gray-600 max-w-[220px] truncate" title={r.counterpartyName ?? ""}>
                      {r.counterpartyName ?? "—"}
                    </td>
                  )}
                  <td className="py-1.5">{fmtDateShort(r.issueDate)}</td>
                  <td className="py-1.5">{fmtDateShort(r.dueDate)}</td>
                  <td className="py-1.5 text-right">{fmtValue(r.invoiceAmount, displayUnit)}</td>
                  <td className="py-1.5 text-right text-gray-500">
                    {r.settledAmount != null ? fmtValue(r.settledAmount, displayUnit) : "—"}
                  </td>
                  <td className="py-1.5 text-right font-medium">{fmtValue(r.openAmount, displayUnit)}</td>
                  <td className={`py-1.5 text-right ${r.daysOverdue > 0 ? "text-red-600 font-medium" : "text-gray-400"}`}>
                    {r.daysOverdue > 0 ? r.daysOverdue : "—"}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className="flex justify-end gap-2">
          <button
            className="text-xs text-gray-400 hover:text-gray-700 disabled:opacity-30"
            disabled={curPage === 0}
            onClick={() => setDetailPage((p) => ({ ...p, [groupKey]: curPage - 1 }))}
          >
            ← Anterior
          </button>
          <span className="text-xs text-gray-400">
            {curPage + 1}/{totalPages}
          </span>
          <button
            className="text-xs text-gray-400 hover:text-gray-700 disabled:opacity-30"
            disabled={curPage >= totalPages - 1}
            onClick={() => setDetailPage((p) => ({ ...p, [groupKey]: curPage + 1 }))}
          >
            Próximo →
          </button>
        </div>
      )}
    </div>
  );
}

function fmtDocument(doc: string): string {
  const d = doc.replace(/\D/g, "");
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  return doc;
}
